import _ from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CommonFacilityListModel } from "src/api/building/building-types";
import { getContractApply } from "src/api/contract/contract-api";
import { getCommonFacilityListAsync } from "src/api/contract/contract-facility-api";
import { ContractManageList } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { PageMeta } from "src/api/public-types";
import { getReservationListAsync, getReservationUsageListAsync } from "src/api/reservation/reservation-api";
import { ReservationListModel, ReservationListParams, ReservationUsageParams } from "src/api/reservation/reservation-types";
import { useModal } from "src/recoil/modalState/hook";
import { downloadExcel, fetchDataWithType, formatPhoneNumber, qsParse, YmdFormat } from "src/utils";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import { reservationColumns } from "../list/columns";
import { ReservationState, reservationUsageHeaders } from "../reservation-types";

// 변환된 데이터를 위한 새로운 타입

const useRv = () => {
  const location = useLocation();
  const { setBaseModal } = useModal();
  const reservationQueryParam: ReservationListParams = useMemo(() => {
    const _queryParams: ReservationListParams = qsParse(location.search);

    // 기본값 설정 로직...
    if (!_queryParams?.page) _queryParams.page = 0;
    if (!_queryParams?.size) _queryParams.size = 20;
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "id", direction: "DESC", nullHandling: "NULLS_LAST" }],
      };
    }
    if (_queryParams?.sort) {
      _queryParams.sort = {
        orders: _queryParams.sort.orders?.map((order) => ({
          ...order,
          nullHandling: "NULLS_LAST",
        })),
      };
    }
    if (!_queryParams?.keyword) {
      _queryParams.keyword = "";
    }

    // URL 파라미터 디코딩
    const queryParamsToDecode = [
      "start",
      "end",
      "keyword",
      "search001",
      "id",
      "parentId",
      "containsFacilityName",
      "containsBuildingName",
      "containsOrganizerMemberNo",
      "containsContractApplyNumber",
    ];
    for (const key of queryParamsToDecode) {
      if (_queryParams?.[key]) {
        _queryParams[key] = decodeURIComponent(_queryParams[key] as string);
      }
    }

    return _queryParams;
  }, [location]);

  const [reservationList, setReservationList] = useState<ReservationListModel[]>();
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [usagePageMeta, setUsagePageMeta] = useState<PageMeta>();
  // 계약 목록 api
  const { executeAsync: getContractList } = useApiOperation(getContractApply);

  // 공용공간 목록 api
  const { executeAsync: getReservationList } = useApiOperation(getReservationListAsync);

  // 예약 소진시간 목록 조회
  const { executeAsync: getReservationUsageList } = useApiOperation(getReservationUsageListAsync);

  const { executeAsync: getCommonFacilityList } = useApiOperation(getCommonFacilityListAsync);

  useEffect(() => {
    getReservations(reservationQueryParam);

    if (reservationQueryParam.start && reservationQueryParam.end) {
      getReservationUsages({ page: 0, size: 20, reservation: { start: reservationQueryParam?.start, end: reservationQueryParam?.end } });
    } else {
      getReservationUsages({ page: 0, size: 20 });
    }
  }, [reservationQueryParam]);

  const changeReservationStatus = (reservationParams: any) => {
    const newReservationParams = { ...reservationParams };
    if (newReservationParams.keyword === "") {
      delete newReservationParams.keyword;
    }
    if (newReservationParams.search001 !== "" && newReservationParams.search001 !== undefined) {
      newReservationParams.search001 = decodeURIComponent(newReservationParams.search001 || "");
    }

    if (newReservationParams.reservationStatus) {
      if (newReservationParams.reservationStatus === "RESERVATION_FAILED") {
        // 예약실패
        newReservationParams.statusCode = ReservationState.INCOMPLETE;
      } else if (newReservationParams.reservationStatus === "WAITING_FOR_USE") {
        // 이용대기
        newReservationParams.statusCode = ReservationState.ACKNOWLEDGED;
        newReservationParams.greaterThanStart = moment().format(YmdFormat.WITH_TIME_ZONE);
      } else if (newReservationParams.reservationStatus === "IN_USE") {
        // 이용중
        newReservationParams.statusCode = ReservationState.ACKNOWLEDGED;
        newReservationParams.lessThanEqualsStart = moment().format(YmdFormat.WITH_TIME_ZONE);
        newReservationParams.greaterThanEnd = moment().format(YmdFormat.WITH_TIME_ZONE);
      } else if (newReservationParams.reservationStatus === "USE_COMPLETED") {
        // 이용완료
        newReservationParams.statusCode = ReservationState.ACKNOWLEDGED;
        newReservationParams.lessThanEqualsEnd = moment().format(YmdFormat.WITH_TIME_ZONE);
      } else if (newReservationParams.reservationStatus === "USE_CANCELLED") {
        // 이용완료
        newReservationParams.statusCode = ReservationState.CANCELLED;
      }
      delete newReservationParams.reservationStatus;
    }
    return newReservationParams;
  };

  // 공용공간 예약 리스트 api
  const getReservations = async (reservationParams: ReservationListParams) => {
    const newReservationParams = changeReservationStatus(reservationParams);
    // 예약 목록 조회
    const {
      data: {
        data: { content },
        meta,
      },
    } = await getReservationList(newReservationParams);

    const reservations = content;

    const contractIds = [...new Set(reservations.map((item) => item.contract.id))].join(",");
    const deskIds =
      [...new Set(reservations.map((item) => item.facility.type === "DESK" && item.facility.id))].filter((id) => id !== false).join(",") || null;
    let deskFacilityList: CommonFacilityListModel[] = [];

    // 좌석 데이터가 있을 때만 api 호출하기
    if (deskIds) {
      // 공용공간 조회

      const {
        data: {
          data: { content: commonFacilityList },
        },
      } = await getCommonFacilityList({ page: 0, size: newReservationParams.size, searchType: "ID", keywordList: deskIds });
      deskFacilityList = commonFacilityList;
    }

    let contractData;
    if (contractIds.length > 0) {
      // 계약 목록 조회
      const {
        data: { data },
      } = await getContractList({ contractIds: contractIds });
      contractData = data;
    }

    const contracts = contractData?.content;

    setNewReservationValues(reservations || [], contracts || [], deskFacilityList || []);
    setPageMeta(meta.pageMeta);
  };
  // 소진시간내역 호출
  const getReservationUsages = async (params: any) => {
    const {
      data: { meta },
    } = await getReservationUsageList(params);
    if (meta) setUsagePageMeta(meta.pageMeta);
  };

  // 새로운 value로 저장 > 계약명 추가 & 엑셀 다운로드 value로 바꾸기
  const setNewReservationValues = (
    reservations: ReservationListModel[],
    contracts: ContractManageList[],
    commonFacilityList: CommonFacilityListModel[],
  ) => {
    const newValues = reservations.map((item) => {
      contracts.forEach((contract) => {
        if (Number(item.contract.id) === Number(contract.contractId)) {
          item = {
            ...item,
            facility: {
              ...item.facility,
              buildingCommonFacilityDeskGroupName:
                commonFacilityList.find((facility) => facility.id === item.facility.id)?.buildingCommonFacilityDeskGroupName || "",
            },
            contract: {
              ...item.contract, //
              contractName: contract.contractName || contract.spaceProductName,
              mbOrganizationName: contract.mbOrganizationName || "",
            },
          };
        }
      });
      return item;
    });
    setReservationList(newValues);
  };

  // 공통 유틸리티 함수들
  const createExcelFileName = (prefix: string, params: ReservationListParams) => {
    const timestamp = moment().format("YYYYMMDDHHmm");
    if (params.start && params.end) {
      return `ctrl.room_${prefix}_${moment(params.start).format("YYYYMMDD")}~${moment(params.end).format("YYYYMMDD")}_${timestamp}`;
    }
    return `ctrl.room_${prefix}_전체_${timestamp}`;
  };

  const showDownloadResult = (success: boolean, error?: any) => {
    setBaseModal({
      isOpen: true,
      title: success ? "엑셀 다운로드가 완료되었습니다." : "엑셀 다운로드에 실패하였습니다.",
      ...(error && { children: String(error) }),
      btnRightTitle: "확인",
      onClick: () => setBaseModal({ isOpen: false }),
    });
  };

  // 계약 데이터 병렬 조회 함수
  const fetchContractDataInBatches = async (contractIds: string[]) => {
    const contractIdChunks = _.chunk([...new Set(contractIds)], 300);
    const contractPromises = contractIdChunks.map((chunk) => getContractList({ contractIds: chunk.join(",") }));
    const contractResponses = await Promise.all(contractPromises);

    return contractResponses.flatMap((response) => response.data.data?.content);
  };

  /** 공용공간 예약목록 엑셀 다운로드 */
  const onDownloadReservations = async (passParams: ReservationListParams) => {
    if (!reservationList) return;

    try {
      const defaultSize = 1000;
      const totalPages = Math.ceil(pageMeta?.totalElements! / defaultSize);

      const newReservationParams = changeReservationStatus({ ...passParams, size: defaultSize });

      // 1. 예약 데이터 조회
      let reservations = await fetchDataWithType<any>(getReservationList, newReservationParams, totalPages, "batched", { batchSize: 8 });
      let deskFacilityList: CommonFacilityListModel[] = [];

      const deskIds =
        [...new Set(reservations.map((item) => item.facility.type === "DESK" && item.facility.id))].filter((id) => id !== false).join(",") || null;

      // 좌석 데이터가 있을 때만 api 호출하기
      if (deskIds) {
        // 공용공간 조회
        const {
          data: {
            data: { content: commonFacilityList },
          },
        } = await getCommonFacilityList({ page: 0, size: defaultSize, searchType: "ID", keywordList: deskIds });
        deskFacilityList = commonFacilityList;

        reservations = reservations.map((item) => {
          deskFacilityList.forEach((facility) => {
            if (item.facility.id === facility.id) {
              item.facility.buildingCommonFacilityDeskGroupName = facility.buildingCommonFacilityDeskGroupName;
            }
          });
          return item;
        });
      }

      // 2. 계약 데이터 조회 및 매핑
      const contracts = await fetchContractDataInBatches(reservations.map((item) => String(item.contract.id)));
      const contractMap = new Map(contracts.map((contract) => [String(contract.contractId), contract]));

      // 3. 계약 정보를 예약 데이터에 추가
      const reservationsWithContract = reservations.map((item) => {
        const contract = contractMap.get(String(item.contract.id));
        return {
          ...item,
          contract: {
            ...item.contract,
            contractName: contract?.contractName || contract?.spaceProductName || item.contract.contractName,
            mbOrganizationName: contract?.mbOrganizationName || item.contract.mbOrganizationName,
            contractApplyNumber: contract?.contractApplyNumber || item.contract.applyNumber,
          },
        };
      });

      // 4. 엑셀 다운로드
      const fileNamePrefix =
        passParams.start && passParams.end
          ? `공용공간예약-${moment(passParams.start).format("YYYYMMDD")}~${moment(passParams.end).format("YYYYMMDD")}`
          : "공용공간예약-전체";

      await downloadExcelByColumns<ReservationListModel>({
        data: reservationsWithContract,
        columns: reservationColumns,
        fileNamePrefix,
        onNoData: () => {
          setBaseModal({
            isOpen: true,
            title: "조회된 데이터가 없습니다.",
            btnRightTitle: "확인",
            onClick: () => setBaseModal({ isOpen: false }),
          });
        },
        onSuccess: () => {
          showDownloadResult(true);
        },
        onError: (error: any) => {
          showDownloadResult(false, error);
        },
      });
    } catch (error) {
      showDownloadResult(false, error);
    }
  };

  /** 소진시간 목록 엑셀 다운로드 */
  const onDownloadReservationUsages = async (passParams: ReservationListParams) => {
    if (!reservationList) return;

    try {
      const defaultSize = 1000;
      const totalPages = Math.ceil(usagePageMeta?.totalElements! / defaultSize);

      // URL 파라미터에서 start, end, sort, page, size 제외한 나머지 추출
      const { start, end, sort, page, size, ...restParams } = passParams;

      // 빈 값 제거 (undefined, null, "" 제외)
      const filteredRestParams = Object.entries(restParams).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      // 1. 소진시간 데이터 조회
      const usageParams: ReservationUsageParams = {
        reservation: {
          ...filteredRestParams, // URL 파라미터 추가 (빈 값 제외)
          start: passParams?.start, // usageParams에 선언된 값이 우선
          end: passParams?.end,
        },
        sort: {
          orders: [
            { property: "reservationId", direction: "DESC", nullHandling: "NULLS_LAST" },
            { property: "order", direction: "ASC", nullHandling: "NULLS_LAST" },
          ],
        },
        size: defaultSize,
      };
      const usageDataList = await fetchDataWithType<any>(getReservationUsageList, usageParams, totalPages, "batched", { batchSize: 8 });

      // 데이터가 없으면 메시지 표시 후 종료
      if (!usageDataList || usageDataList.length === 0) {
        setBaseModal({
          isOpen: true,
          title: "소진시간 내역이 없습니다.",
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
        });
        return;
      }

      // 2. 예약 데이터 조회 및 매핑
      const reservationIds = [...new Set(usageDataList.map((usageItem) => usageItem.reservationId))];
      const chunkArray = (array: string[], size: number) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
          chunks.push(array.slice(i, i + size));
        }
        return chunks;
      };
      const chunkedReservationIds = chunkArray(reservationIds, 500);
      const reservations = [];

      for (const chunk of chunkedReservationIds) {
        const reservationIdsToStr = chunk.join(", ");
        const response = await getReservationList({ id: reservationIdsToStr });
        reservations.push(...(response.data.data?.content || []));
      }
      // const reservations = reservationsResponses.flatMap((response) => response.data.data?.content);

      // 예약 데이터와 summary 매핑
      const reservationMap = new Map(reservations.map((reservation) => [reservation.id, reservation]));

      // 3. 계약 데이터 조회 및 매핑 (usageData의 contract.id 사용)
      const contractIds = [...new Set(usageDataList.map((usageItem) => String(usageItem.contract.id)))].filter((id) => id);
      let contractResponse: any;
      if (contractIds.length > 0) {
        const contracts = await fetchContractDataInBatches(contractIds);
        contractResponse = contracts;
      }
      const contractMap = new Map(contractResponse.map((contract: any) => [String(contract.contractId), contract]));

      // 4. usageData에 summary와 contract 정보 추가
      const enhancedUsageDataList = usageDataList.map((usageItem) => {
        const reservation = reservationMap.get(usageItem.reservationId);
        // usageData에서 직접 계약 ID를 사용
        const contract = contractMap.get(String(usageItem.contract.id));

        return {
          ...usageItem,
          summary: reservation?.summary || null,
          contract: contract || null,
        };
      });

      // 5. 데이터 변환
      const excelData = enhancedUsageDataList.map((enhancedItem) => transformUsageToExcelFormat(enhancedItem));

      // 3. 엑셀 다운로드
      await downloadExcel({
        data: excelData,
        fileName: createExcelFileName("공용공간예약_소진시간", passParams),
        header: reservationUsageHeaders.map((header) => header.label),
      });

      showDownloadResult(true);
    } catch (error) {
      showDownloadResult(false, error);
    }
  };

  const transformUsageToExcelFormat = (item: any) => {
    if (!item) return;
    return {
      reservationId: item.reservationId,
      // item.contract 는 위에서 contractMap(ContractManageList)으로 교체되므로 id 가 아니라 contractId 다
      "contract.id": item.contract?.contractId || "-",
      "contract.contractName": item.contract?.contractName,
      "contract.mbOrganizationName": item.contract?.mbOrganizationName || "-",
      facilityBuildingName: item.facility?.building?.name,
      facilityType: getFacilityTypeText(item.facility?.type),
      summary: item.summary || "-",
      facilityId: item.facility?.id,
      facilityName: item.facility?.name,
      start: moment(item.start).format(YmdFormat.YYYY_MM_DD_HH_MM),
      end: moment(item.end).format(YmdFormat.YYYY_MM_DD_HH_MM),
      duration: item.duration,
      organizerMemberNo: item.organizer?.memberNo,
      organizerPhoneNumber: formatPhoneNumber(item.organizer.phoneNumber),
      organizerUserEmail: item.organizer?.userEmail,
    };
  };

  const getFacilityTypeText = (type: string) => {
    switch (type) {
      case "DESK":
        return "좌석";
      case "MEETING_ROOM":
        return "회의실";
      default:
        return "편의시설";
    }
  };

  return {
    reservationQueryParam,
    reservationList,
    pageMeta,
    getReservations,
    getReservationUsageList,
    onDownloadReservations,
    onDownloadReservationUsages,
  };
};

export default useRv;
